// Book catalog + chapter structure, captured from the live site 2026-10-14.
//
// CHAPTER TEXT IS SERVED BY THE SERVER (since Oct 27, 2026): the reader
// fetches each chapter from /api/books/:id/chapters/:n — free chapters
// publicly, paid chapters only for entitled accounts. The `text` fields
// below are legacy placeholder slots and are NOT used for delivery; real
// manuscript text lives server-side only (server/src/content-data.ts,
// generated by scripts/build-content.mjs) so it can never be lifted from
// this bundle. Do NOT paste manuscript text in here.
export const MANUSCRIPT_PLACEHOLDER = "CONTENT FROM MANUSCRIPT — TO BE INSERTED";

export interface Chapter {
  /** Sidebar label, e.g. "Prologue" or "Chapter One". */
  label: string;
  /** Optional captured chapter title (Trust has titles; Doors does not). */
  title?: string;
  free: boolean;
  text: string;
  /**
   * Optional photo shown at the start of the chapter in the reader
   * (public stock photo — path is a served static asset, caption is the
   * photographer credit). Only Quietly Ready uses this (Oct 29, 2026).
   */
  image?: { src: string; caption: string };
}

export interface Book {
  id: "doors1" | "trust" | "quietlyready";
  bookPath: string;
  readerPath: string;
  genre: string;
  title: string;
  tagline: string;
  blurb: string;
  chaptersLabel: string;
  wordsLabel: string;
  priceLabel: string;
  priceCents: number;
  /** Book-page label for the free-opening button. */
  freeLabel: string;
  /** Optional cover image (public path). Books without one use the icon tile. */
  coverSrc?: string;
  chapters: Chapter[];
}

const ch = (
  label: string,
  free: boolean,
  title?: string,
  image?: { src: string; caption: string },
): Chapter => ({
  label,
  title,
  free,
  text: MANUSCRIPT_PLACEHOLDER,
  ...(image ? { image } : {}),
});

const DOORS_NUMBERS = [
  "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
  "Seventeen",
];

export const DOORS: Book = {
  id: "doors1",
  bookPath: "/book/doors",
  readerPath: "/reader/doors1",
  genre: "A Spiritual Mystery · Book One",
  title: "Doors — Book One",
  tagline: "what was never taken",
  blurb:
    "A watched child. A hidden inheritance. A door that opens only for the one it recognizes. Truth, he learns, cannot be stolen — only delayed.",
  chaptersLabel: "18 Chapters",
  wordsLabel: "63k Words",
  priceLabel: "$2.99",
  priceCents: 299,
  freeLabel: "Start with the free Prologue & Chapter One",
  chapters: [
    ch("Prologue", true),
    ...DOORS_NUMBERS.map((n, i) => ch(`Chapter ${n}`, i === 0)),
  ],
};

export const TRUST: Book = {
  id: "trust",
  bookPath: "/book/trust",
  readerPath: "/reader/trust",
  genre: "Personal Finance · Estate & Legacy",
  title: "The Trust I Built Before I Needed It",
  tagline: "build it before you need it",
  blurb:
    "One investor's honest journey from buying assets to protecting them — trusts, jurisdictions, and an actual irrevocable trust built before it was ever needed.",
  chaptersLabel: "10 Chapters",
  wordsLabel: "7k Words",
  priceLabel: "$18.88",
  priceCents: 1888,
  freeLabel: "Start with free Chapter One",
  chapters: [
    ch("Chapter One", true, "The Day I Started Planning for Success"),
    ch("Chapter Two", false, "The Day I Realized Wealth Is an Operating System"),
    ch("Chapter Three", false, "Revocable vs. Irrevocable: The First Decision That Changed Everything"),
    ch("Chapter Four", false, "The Rule That Changed Everything"),
    ch("Chapter Five", false, "Why the State You Choose May Matter More Than the Trust You Write"),
    ch("Chapter Six", false, "The Day I Stopped Looking for the Perfect Trust"),
    ch("Chapter Seven", false, "Building My Trust on Paper Before Paying an Attorney"),
    ch("Chapter Eight", false, "The Blueprint Was Never About Me"),
    ch("Chapter Nine", false, "The States Everyone Watches"),
    ch("Chapter Ten", false, "The Trust I Built Before I Needed It"),
  ],
};

export const QUIETLY_READY: Book = {
  id: "quietlyready",
  bookPath: "/book/quietly-ready",
  readerPath: "/reader/quietlyready",
  genre: "Household Preparedness · A Practical Guide",
  title: "Quietly Ready",
  tagline:
    "A Calm, Staged Guide to Household Preparedness — Food, Water, and the Layers That Matter",
  blurb:
    "Preparedness built in layers, not panic: food and water first, then cooking without power, documents, and the monthly rhythm that keeps it all alive — one calm stage at a time, on an ordinary grocery budget.",
  chaptersLabel: "14 Chapters + Appendix",
  wordsLabel: "9.3k Words",
  priceLabel: "$18.88",
  priceCents: 1888,
  freeLabel: "Read the opening free",
  coverSrc: "/quietly-ready-cover.jpg",
  chapters: [
    ch("A Note Before We Start", true),
    ch("Introduction", true, "Why I Prep"),
    ch("Chapter One", false, "The staging system — layers, not a haul", {
      src: "/qr-pantry.jpg",
      caption: "Photo: Rachel Claire / Pexels",
    }),
    ch("Chapter Two", false, "How to buy it — let the sales cycle do the saving"),
    ch("Chapter Three", false, "Water — the honest math", {
      src: "/qr-water.jpg",
      caption: "Photo: Julia M Cameron / Pexels",
    }),
    ch("Chapter Four", false, "Food — the dry core", {
      src: "/qr-dry-goods.jpg",
      caption: "Photo: Agung Pandit Wiguna / Pexels",
    }),
    ch("Chapter Five", false, "The canned layer, the fats, and the small things", {
      src: "/qr-canned.jpg",
      caption: "Photo: Ron Lach / Pexels",
    }),
    ch("Chapter Six", false, "Cooking without power", {
      src: "/qr-cooking.jpg",
      caption: "Photo: Tima Miroshnichenko / Pexels",
    }),
    ch("Chapter Seven", false, "The animals", {
      src: "/qr-animals.jpg",
      caption: "Photo: Viktoria B. / Pexels",
    }),
    ch("Chapter Eight", false, "Hygiene and the sickness layer"),
    ch("Chapter Nine", false, "The medical layer — staged, and handled with your doctor"),
    ch("Chapter Ten", false, "Power and communications — the works-without-it principle"),
    ch("Chapter Eleven", false, "Documents and money readiness"),
    ch("Chapter Twelve", false, "Low profile — the quiet rule"),
    ch("Chapter Thirteen", false, "The go-bag, and staying put vs. leaving", {
      src: "/qr-go-bag.jpg",
      caption: "Photo: Kindel Media / Pexels",
    }),
    ch("Chapter Fourteen", false, "Keeping it alive — rotation and the monthly rhythm"),
    ch("Appendix", false, "Worksheets and Checklists"),
  ],
};

export const BOOKS: Book[] = [DOORS, TRUST, QUIETLY_READY];

export function bookByReaderId(id: string): Book {
  return BOOKS.find((b) => b.id === id) ?? DOORS;
}
